<script setup lang="ts">
const productContent = useProductContentStore()
const products = useProductVariantsStore()
const route = useRoute<"subject">()

const { data: allLicences } = useLicences()

const folder = computed(() => {
  const matchingFolder = productContent.folders.find((p) => {
    return p.id === productContent.activeSubject?.parentLocationId
  })
  return matchingFolder || null
})

const decodedFolderTitle = computed(() => {
  if (folder.value?.title) {
    return formatForURL(folder.value?.title)
  }

  return undefined
})

const subjectTitle = computed(
  () =>
    productContent.activeSubject?.shortTitle ||
    productContent.activeSubject?.title,
)

const showSubjectTitle = computed(
  () =>
    subjectTitle.value &&
    typeof productContent.activeSubject?.priority === "number",
)
</script>

<template>
  <UFeatureFlag
    class="fixed left-0 top-0 z-50 w-fit p-4 text-gray-400"
  >
    <UButton
      to="/"
      variant="quite"
      size="sm"
      theme="white"
    >
      kunne.no
    </UButton>
  </UFeatureFlag>
  <div class="flex size-full items-center justify-center bg-[--theme-darkest]">
    <div
      class="flex w-[90vw] flex-col items-center justify-center md:w-[85vw] md:flex-row md:items-start md:gap-16 2xl:w-[80vw]"
    >
      <USideNav
        :data="productContent.articleNav"
        :licence="!!allLicences?.length"
        :product="products.activeVariant"
        class="md:w-full"
        page-type="subject"
      >
        <li class="mt-0 w-fit md:m-0 md:mt-1">
          <NuxtLink
            :to="
              folder?.title
                ? `/produkter/${route.params.product}/${route.params.variant}#${decodedFolderTitle}`
                : `/produkter/${route.params.product}/${route.params.variant}`
            "
            class="flex items-center"
          >
            <UIcon
              name="arrow-left"
              size="sm"
              class="mr-3.5"
            />
            <h4
              class="max-w-[200px] truncate font-extrabold lg:max-w-[300px]"
            >
              Tilbake {{ folder ? `til ${folder.title}` : "" }}
            </h4>
          </NuxtLink>
        </li>
        <li
          v-if="showSubjectTitle"
          class="justify-content-between mt-8 hidden w-full items-start pb-3 text-white md:flex md:border-b-2 md:border-white md:pb-2"
        >
          <div class="flex">
            <span
              class="subject-number menu-heading-large mr-3.5 flex w-[18px] items-center justify-center"
            >
              {{ productContent.activeSubject?.priority }}
            </span>
            <span class="menu-heading-large">
              {{ subjectTitle }}
            </span>
          </div>
          <UContentFreeLabel
            v-if="productContent.activeSubject?.showFreeLabel"
            class="ml-auto mr-2"
            :small="true"
          />
        </li>
      </USideNav>
      <main
        class="size-full max-w-[350px] lg:max-w-[45em] <md:flex <md:flex-col <md:items-center <md:justify-center"
      >
        <UMyProfileButton
          :path="[
            route.params.product,
            route.params.variant,
            route.params.subject,
          ]"
        />
        <div
          v-if="productContent.noSubjectsFound"
          class="richtext pb-32 text-white md:pt-[175px] 2xl:pt-[210px]"
        >
          <h1>Fant ikke kapittelet</h1>
          <NuxtLink
            class="mt-6 block"
            :to="`/produkter/${route.params.product}/${route.params.variant}`"
          >
            Ta meg til forsiden av Kunne
            {{ products.activeProduct?.productTitle ?? "" }}
          </NuxtLink>
        </div>
        <template v-else>
          <div
            class="mx-auto flex w-full flex-col gap-4 self-start text-pretty pt-6"
          >
            <UContentFreeLabel
              v-if="productContent.activeSubject?.showFreeLabel"
              :small="true"
              class="md:hidden"
            />
            <h1
              v-if="
                showSubjectTitle &&
                  !route.fullPath.endsWith('min-side')
              "
              class="font-national2condensed text-mobile-article-h2 text-white md:hidden"
            >
              {{
                `${productContent.activeSubject?.priority}. ${subjectTitle}`
              }}
            </h1>
          </div>
          <slot />
        </template>
      </main>
    </div>
  </div>
  <UBottomPanel />
</template>

<style scoped>
.menu-heading-large {
  @apply font-national2 text-[20px] font-bold not-italic !leading-6;
}
</style>
